import React from 'react'

import { HiMiniXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { Skeleton } from '@/components/common/Skeleton'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useTag } from '@/hooks/tags/useTag'

import { UUID } from '@/types/Id'

interface TagProps {
  tagId: UUID
  className?: string
  onClick?: () => void
  onDelete?: () => void
}

export const Tag: React.FC<TagProps> = ({
  tagId,
  className,
  onClick,
  onDelete,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: tag, isLoading: isLoadingTag } = useTag({
    orgId: activeOrgId,
    tagId,
  })

  if (isLoadingTag) {
    return <Skeleton className="w-[10px] h-[10px] rounded-full" />
  }

  if (!tag) {
    return null
  }

  return (
    <button
      type="button"
      className={twMerge(
        'flex min-w-0 max-w-full items-center gap-x-[6px] overflow-hidden rounded-full border border-primary-200 px-[7px] text-[13px] font-medium text-primary-900 dark:border-primary-800 dark:text-primary-100',
        onClick
          ? 'hover:bg-primary-100 dark:hover:bg-primary-800 transition-colors duration-100'
          : 'cursor-default',
        className,
      )}
      onClick={onClick}
      tabIndex={onClick ? 0 : -1}
    >
      <ColorIndicator color={tag.color} className="w-[10px] h-[10px]" />
      <span className="min-w-0 truncate whitespace-nowrap">{tag.name}</span>
      {onDelete && (
        <button onClick={onDelete} type="button">
          <HiMiniXMark className="w-[14px] h-[14px] text-primary-700 dark:text-primary-300" />
        </button>
      )}
    </button>
  )
}
